import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import SeoHelmet from "@/components/SeoHelmet";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Sparkles, Check, Star, ArrowRight, Chrome, 
  Search, FileText, Zap, MousePointerClick, 
  ChevronDown, ChevronUp, MessageSquare, 
  Globe, Users, Facebook, Instagram, Linkedin
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";

const ChromeStoreLogo = ({ className }: { className?: string }) => (
  <img 
    src="/chrome-store.png" 
    alt="Chrome Web Store" 
    className={className}
  />
);

const Index = () => {
  const navigate = useNavigate();
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  const [activeJobId, setActiveJobId] = useState<string>("engineering");
  const [isTailoring, setIsTailoring] = useState(false);
  const [isSubscribing, setIsSubscribing] = useState<string | null>(null);
  const { user } = useAuth();

  const isLocalhost = typeof window !== 'undefined' && (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.startsWith('192.168.') ||
    window.location.hostname.endsWith('.local')
  );

  // Trigger a "tailoring" animation when changing jobs
  useEffect(() => {
    setIsTailoring(true);
    const timer = setTimeout(() => setIsTailoring(false), 1200);
    return () => clearTimeout(timer);
  }, [activeJobId]);

  const testimonials = [
    { name: "Sarah J.", role: "Product Manager @ Google", content: "Went from 0 interviews to 5 in two weeks. The extension is a game changer.", rating: 5 },
    { name: "Mark D.", role: "Software Engineer @ Meta", content: "ATS finally loves my CV. The tailoring is subtle but extremely effective.", rating: 5 },
    { name: "Elena R.", role: "Marketing Lead @ Startup", content: "The 'Review First' mode gave me the confidence to apply with AI. Flawless.", rating: 5 },
  ];

  const faqs = [
    { 
      q: "Will it invent experience I don't have?", 
      a: "No. Alvaru uses your existing experience as the foundation. It re-emphasizes your relevant skills and matches the job's vocabulary, but never invents false history." 
    },
    { 
      q: "Does it work with LinkedIn Easy Apply?", 
      a: "Yes! Our Chrome Extension is specifically designed for auto apply LinkedIn functionality. It detects Easy Apply forms and fills them with your tailored CV and information in one click." 
    },
    { 
      q: "How is this different from ChatGPT?", 
      a: "Unlike a general AI, Alvaru is a specialized AI CV optimiser. We use custom prompts tuned specifically for ATS (Applicant Tracking Systems) and job algorithms, with a workflow built for browser integration." 
    },
  ];

  const jobsData = {
    engineering: {
      label: "ENGINEERING",
      title: "Senior Full Stack Engineer @ DataNimbus",
      jd: "We're looking for an engineer to lead our <span class='hl-orange'>distributed systems</span> efforts. You will build <span class='hl-orange'>scalable APIs</span> using Node.js and <span class='hl-orange'>PostgreSQL</span>. Experience with <span class='hl-orange'>real-time data</span> and <span class='hl-orange'>performance tuning</span> is critical. We value <span class='hl-orange'>clean code</span> and architecture.",
      cv_header: "Leo Vance",
      cv_title: "Full Stack Engineer, CloudLink",
      cv_content: "Senior engineer with 7 years expertice in <span class='hl-green'>distributed systems</span>. Architected <span class='hl-green'>scalable APIs</span> for high-traffic financial tools. Optimized <span class='hl-green'>PostgreSQL</span> queries for <span class='hl-green'>real-time data</span> processing, achieving a 40% gain in <span class='hl-green'>performance tuning</span> while maintaining <span class='hl-green'>clean code</span> standards.",
      match: 94
    },
    product: {
      label: "PRODUCT",
      title: "Lead Product Designer @ FlowState",
      jd: "FlowState needs a designer to own our <span class='hl-orange'>design system</span>. You’ll create <span class='hl-orange'>interactive prototypes</span> and lead <span class='hl-orange'>user research</span> for our mobile app. We prioritize <span class='hl-orange'>accessibility</span> and <span class='hl-orange'>visual polish</span>. You should thrive in <span class='hl-orange'>cross-functional</span> teams.",
      cv_header: "Sophie Chen",
      cv_title: "Product Designer, DesignFlow",
      cv_content: "Lead designer specializing in <span class='hl-green'>design system</span> architecture. Created <span class='hl-green'>interactive prototypes</span> that reduced dev time by 30%. Helmed <span class='hl-green'>user research</span> to improve <span class='hl-green'>accessibility</span> ratings. Known for <span class='hl-green'>visual polish</span> in <span class='hl-green'>cross-functional</span> environments.",
      match: 88
    },
    growth: {
      label: "GROWTH",
      title: "Growth Marketing Manager @ SwiftPay",
      jd: "SwiftPay is looking for a data-driven marketer to scale <span class='hl-orange'>user acquisition</span>. You’ll manage <span class='hl-orange'>paid social</span> budgets and optimize <span class='hl-orange'>conversion funnels</span>. Must be an expert in <span class='hl-orange'>retention strategies</span> and <span class='hl-orange'>A/B testing</span> to drive <span class='hl-orange'>LTV</span> growth.",
      cv_header: "Marcus Thorne",
      cv_title: "Growth Lead, PayFast",
      cv_content: "Growth marketer with a focus on <span class='hl-green'>user acquisition</span> at scale. Managed $500k/mo <span class='hl-green'>paid social</span> spend while improving <span class='hl-green'>conversion funnels</span>. Developed <span class='hl-green'>retention strategies</span> using rigorous <span class='hl-green'>A/B testing</span> that resulted in a 15% lift in <span class='hl-green'>LTV</span>.",
      match: 91
    }
  };

  const activeJob = jobsData[activeJobId as keyof typeof jobsData];

  const openChromeStore = () => {
    const url = 'https://chromewebstore.google.com/detail/alvaru-smart-job-apply/laoahcalgdmfecdbiodloilhcafeoafl?authuser=0&hl=en';
    const chrome = (window as any).chrome;
    if (typeof chrome !== 'undefined' && chrome.tabs) {
      chrome.tabs.create({ url });
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleSubscribe = async (priceId: string, planName: string) => {
    if (!user) {
      toast.info("Please sign in to subscribe to " + planName);
      navigate("/auth");
      return;
    }

    setIsSubscribing(priceId);
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout-session", {
        body: { 
          priceId, 
          userId: user.id,
          userEmail: user.email 
        }
      });

      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err: any) {
      console.error("Subscription error:", err);
      toast.error("Could not start checkout: " + (err.message || "Unknown error"));
    } finally {
      setIsSubscribing(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6] dark:bg-[#0A0F1E] text-slate-900 dark:text-white transition-colors duration-300 selection:bg-cyan-500/30 font-body overflow-x-hidden">
      <SeoHelmet
        title="AI CV Tailoring & Auto-Apply"
        description="AI CV tailoring that rewrites your resume for every job. Auto-apply on LinkedIn and Indeed. Get more interviews. Start free."
        canonical="https://www.alvaru.com/"
        schema={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "Alvaru",
          "url": "https://www.alvaru.com",
          "applicationCategory": "Productivity",
          "operatingSystem": "Web, Chrome Extension",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.8",
            "ratingCount": "15"
          }
        }}
      />
      <Navbar />

      {/* HERO SECTION */}
      <section className="relative pt-32 pb-20 overflow-hidden min-h-[90vh] flex items-center">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[600px] bg-gradient-to-b from-cyan-500/10 dark:from-cyan-900/20 to-transparent pointer-events-none -z-10" />
        <div className="container mx-auto px-6">
          <div className="flex flex-col lg:flex-row items-center gap-16">
            <div className="flex-1 text-center lg:text-left">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-600 dark:text-cyan-400 text-[10px] font-bold uppercase tracking-widest mb-8"
              >
                <Sparkles className="w-3 h-3" />
                Next-Gen AI CV Tailoring
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className="text-4xl md:text-5xl lg:text-7xl font-heading font-bold tracking-tight leading-[1.05] mb-8"
              >
                Land your dream job with <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-blue-600 dark:from-cyan-400 dark:to-blue-500">AI-powered CV tailoring.</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.4 }}
                className="text-base md:text-xl text-slate-600 dark:text-slate-400 mb-10 leading-relaxed font-body max-w-xl mx-auto lg:mx-0"
              >
                Alvaru is the only CV optimizer that understands job descriptions better than recruiter filters. Don't just apply—dominate with the perfect resume for every role.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.6 }}
                className="flex flex-col sm:flex-row items-center gap-4 mb-14 justify-center lg:justify-start"
              >
                <Button 
                  size="lg" 
                  className="bg-cyan-500 hover:bg-cyan-600 dark:bg-cyan-500 dark:hover:bg-cyan-400 text-white dark:text-black font-bold text-base px-10 py-6 rounded-xl shadow-2xl shadow-cyan-500/20 group h-auto font-heading"
                  onClick={() => navigate("/app")}
                >
                  Get More Interviews
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="border-slate-300 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-900 text-slate-900 dark:text-white font-bold text-base px-10 py-6 rounded-xl h-auto font-heading transition-colors"
                  onClick={openChromeStore}
                >
                  <ChromeStoreLogo className="w-8 h-8 mr-3" />
                  Add to Chrome
                </Button>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 0.8 }}
                className="flex flex-wrap justify-center lg:justify-start gap-x-8 gap-y-4 text-[10px] font-bold text-slate-500 uppercase tracking-widest font-heading"
              >
                <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-cyan-600 dark:text-cyan-500" /> LinkedIn, Indeed, Reed</span>
                <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-cyan-600 dark:text-cyan-500" /> Auto apply support</span>
                <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-cyan-600 dark:text-cyan-500" /> 100% Free to start</span>
              </motion.div>
            </div>

            <div className="flex-1 hidden lg:block">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 1, delay: 0.4, ease: "easeOut" }}
              >
                <img 
                  src={isLocalhost ? "/Gemini_Generated_Image_jlf290jlf290jlf2.png" : "/hero-visual.jpg"} 
                  alt="Alvaru AI CV tailoring interface" 
                  className="w-full h-auto object-contain dark:hidden"
                  loading="lazy"
                  sizes="(max-width: 768px) 100vw, 50vw"
                />
                <img 
                  src={isLocalhost ? "/Gemini_Generated_Image_jlf290jlf290jlf2.png" : "/hero-visual-dark.jpg"} 
                  alt="Alvaru AI CV tailoring interface" 
                  className="w-full h-auto object-contain hidden dark:block"
                  loading="lazy"
                  sizes="(max-width: 768px) 100vw, 50vw"
                />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* DEMO VIDEO SECTION */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-6">
          Stop sending the same CV to every job.
        </h2>
        <video
          src="/alvaru-demo.mp4"
          autoPlay
          muted
          loop
          playsInline
          className="w-full rounded-xl shadow-2xl border border-gray-200"
        />
      </div>

      {/* SOCIAL PROOF BAR */}
      <section className="bg-slate-200/50 dark:bg-slate-900/30 border-y border-slate-300/50 dark:border-slate-800/50 py-6">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4">
            <div className="flex -space-x-2">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="w-8 h-8 rounded-full border-2 border-white dark:border-[#0A0F1E] bg-slate-200 dark:bg-slate-800 flex items-center justify-center overflow-hidden">
                  <img src={`https://i.pravatar.cc/100?img=${i + 15}`} alt="" />
                </div>
              ))}
            </div>
            <div className="flex flex-col items-center md:items-start">
              <div className="flex gap-0.5 mb-0.5">
                {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-3.5 h-3.5 fill-cyan-600 dark:fill-cyan-500 text-cyan-500" />)}
              </div>
              <p className="text-xs font-medium text-slate-600 dark:text-slate-400">Join 1,000+ job seekers already using our AI CV optimiser</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50/50 dark:bg-white/[0.01] border-b border-slate-100 dark:border-white/5 overflow-hidden">
        <div className="container mx-auto px-12 md:px-24">
          <motion.p 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-[10px] md:text-[11px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-[0.4em] text-center mb-12"
          >
            People are tailoring resumes for
          </motion.p>
          <div className="relative flex overflow-hidden">
            <motion.div 
              animate={{ x: ["0%", "-50%"] }}
              transition={{ 
                x: {
                  repeat: Infinity,
                  repeatType: "loop",
                  duration: 25,
                  ease: "linear",
                },
              }}
              className="flex items-center gap-12 md:gap-24 opacity-60 dark:opacity-40 whitespace-nowrap"
            >
              {[
                { name: "Google", className: "text-xl md:text-2xl font-serif italic font-medium" },
                { name: "McKinsey", className: "text-lg md:text-xl font-serif font-bold" },
                { name: "DELOITTE", className: "text-base md:text-lg font-bold tracking-tighter" },
                { name: "amazon", className: "text-xl md:text-2xl font-bold italic tracking-tighter" },
                { name: "Goldman Sachs", className: "text-lg md:text-xl font-serif font-semibold" },
                { name: "TESLA", className: "text-base md:text-lg font-light tracking-[0.3em] pt-1" },
                { name: "Pfizer", className: "text-xl md:text-2xl font-serif italic" },
                { name: "Unilever", className: "text-lg md:text-xl font-serif font-medium" },
              ].map((company, idx) => (
                <span key={idx} className={`${company.className} text-slate-900 dark:text-white shrink-0`}>
                  {company.name}
                </span>
              ))}
              
              {/* Duplicate set for seamless loop */}
              {[
                { name: "Google", className: "text-xl md:text-2xl font-serif italic font-medium" },
                { name: "McKinsey", className: "text-lg md:text-xl font-serif font-bold" },
                { name: "DELOITTE", className: "text-base md:text-lg font-bold tracking-tighter" },
                { name: "amazon", className: "text-xl md:text-2xl font-bold italic tracking-tighter" },
                { name: "Goldman Sachs", className: "text-lg md:text-xl font-serif font-semibold" },
                { name: "TESLA", className: "text-base md:text-lg font-light tracking-[0.3em] pt-1" },
                { name: "Pfizer", className: "text-xl md:text-2xl font-serif italic" },
                { name: "Unilever", className: "text-lg md:text-xl font-serif font-medium" },
              ].map((company, idx) => (
                <span key={`dup-${idx}`} className={`${company.className} text-slate-900 dark:text-white shrink-0`}>
                  {company.name}
                </span>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* THE PROBLEM SECTION */}
      <section className="py-24 bg-white dark:bg-[#05080F] relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-cyan-500/5 dark:bg-cyan-500/10 rounded-full blur-[120px] pointer-events-none" />
        
        <div className="container mx-auto px-6 text-center relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="space-y-4 mb-16">
              {[
                { text: "You've applied to dozens of jobs.", color: "text-slate-900 dark:text-white" },
                { text: "You've rewritten your CV ten times.", color: "text-slate-400 dark:text-slate-500" },
                { text: "You're still getting ignored.", color: "text-slate-900 dark:text-white" }
              ].map((line, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.2 }}
                >
                  <h2 className={`text-2xl md:text-4xl lg:text-5xl font-heading font-bold tracking-tight ${line.color}`}>
                    {line.text}
                  </h2>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="inline-block relative"
            >
              <div className="absolute -inset-4 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 blur-xl rounded-[2rem] -z-10" />
              <div className="bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-cyan-500/20 px-8 md:px-12 py-10 rounded-[2rem] shadow-2xl">
                <p className="text-3xl md:text-5xl font-heading font-bold leading-tight">
                  <span className="text-slate-900 dark:text-white">Alvaru provides a </span>
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-blue-600 dark:from-cyan-400 dark:to-blue-400">
                    professional CV rewriter.
                  </span>
                  <br className="hidden md:block" />
                  <span className="text-slate-900 dark:text-white italic"> Instantly.</span>
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="py-24 bg-transparent">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">How to Tailor CV to Job Description in 4 Steps</h2>
            <p className="text-slate-500 dark:text-slate-400 text-base font-body">Our AI CV optimiser automates the entire tailoring process.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: FileText, title: "Upload Once", desc: "Upload your master CV one time. We'll use this as the primary source for our CV rewriter." },
              { icon: ChromeStoreLogo, title: "Install Extension", desc: "Add our auto apply extension to Chrome. It's safe, fast, and light." },
              { icon: Search, title: "Browse Jobs", desc: "Head to LinkedIn or Indeed. Our AI detects listings for auto apply support." },
              { icon: MousePointerClick, title: "Click Apply", desc: "Hit 'Tailor & Apply' and watch as we create an ATS friendly CV in real-time." }
            ].map((step, i) => (
              <div key={i} className="group relative p-7 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:border-cyan-500/30 shadow-sm dark:shadow-none transition-all">
                <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-cyan-500 text-white dark:text-black flex items-center justify-center font-bold text-xs">
                  {i + 1}
                </div>
                <step.icon className="w-10 h-10 text-cyan-600 dark:text-cyan-500 mb-5 group-hover:scale-105 transition-transform" />
                <h3 className="text-lg font-heading font-bold mb-3">{step.title}</h3>
                <p className="text-slate-500 dark:text-slate-400 text-xs leading-relaxed font-body">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LIVE DEMO SECTION */}
      <section className="py-24 bg-slate-200/20 dark:bg-slate-900/20 relative">
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4 tracking-tight">Try it yourself — <span className="italic text-cyan-600 dark:text-cyan-400">pick a job below</span></h2>
            <p className="text-slate-600 dark:text-slate-400 text-base font-body max-w-2xl mx-auto">See how our AI CV optimiser adapts your application for different industries instantly.</p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 max-w-6xl mx-auto rounded-3xl overflow-hidden border border-slate-200 dark:border-slate-800 shadow-2xl bg-white dark:bg-slate-950">
            {/* JOB DESCRIPTION SIDE */}
            <div className="p-8 md:p-12 border-b lg:border-b-0 lg:border-r border-slate-200 dark:border-slate-800">
              <div className="flex justify-between items-center mb-8">
                <h3 className="text-[10px] font-bold uppercase tracking-widest text-slate-500 font-heading">JOB DESCRIPTION</h3>
                <div className="flex gap-2 p-1 bg-slate-100 dark:bg-slate-900 rounded-lg">
                  {Object.keys(jobsData).map((id) => (
                    <button
                      key={id}
                      onClick={() => setActiveJobId(id)}
                      className={`px-4 py-1.5 rounded-md text-[10px] font-bold transition-all uppercase tracking-wider font-heading ${
                        activeJobId === id 
                        ? "bg-white dark:bg-slate-800 text-cyan-600 dark:text-cyan-400 shadow-sm" 
                        : "text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                      }`}
                    >
                      {jobsData[id as keyof typeof jobsData].label}
                    </button>
                  ))}
                </div>
              </div>
              
              <AnimatePresence mode="wait">
                <motion.div 
                  key={activeJobId}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-6 text-sm md:text-base text-slate-700 dark:text-slate-300 leading-relaxed font-body"
                >
                  <div>
                    <p className="font-bold mb-4 text-lg text-slate-900 dark:text-white font-heading">{activeJob.title}</p>
                    <div 
                      className="demo-content"
                      dangerouslySetInnerHTML={{ __html: activeJob.jd }}
                    />
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
            
            {/* TAILORED CV SIDE */}
            <div className="p-8 md:p-12 relative flex flex-col bg-slate-50/50 dark:bg-slate-900/10">
              <div className="flex justify-between items-center mb-8">
                <h3 className="text-[10px] font-bold uppercase tracking-widest text-slate-500 font-heading">YOUR TAILORED CV</h3>
                <span className={`inline-flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-widest transition-opacity duration-300 ${isTailoring ? 'opacity-100' : 'opacity-0'}`}>
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                  <span className="text-green-600 dark:text-green-400">Tailoring Now</span>
                </span>
              </div>
              
              <AnimatePresence mode="wait">
                <motion.div 
                  key={activeJobId}
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 1.02 }}
                  transition={{ duration: 0.5 }}
                  className="flex-1"
                >
                  <div className="mb-6">
                    <h4 className="text-xl md:text-2xl font-bold font-heading mb-1 text-slate-900 dark:text-white">{activeJob.cv_header}</h4>
                    <p className="text-[9px] text-slate-400 uppercase tracking-[0.15em] font-bold">CONTACT • LOCATION • PORTFOLIO</p>
                  </div>
                  
                  <div className="space-y-6">
                    <div>
                      <h5 className="text-[9px] font-bold uppercase tracking-widest border-b border-slate-200 dark:border-slate-800 pb-1 mb-2 text-slate-400">SUMMARY</h5>
                      <div 
                        className="text-[13px] text-slate-600 dark:text-slate-400 leading-relaxed font-body demo-content"
                        dangerouslySetInnerHTML={{ __html: activeJob.cv_content.split('. ')[0] + '.' }}
                      />
                    </div>
                    
                    <div>
                      <h5 className="text-[9px] font-bold uppercase tracking-widest border-b border-slate-200 dark:border-slate-800 pb-1 mb-2 text-slate-400">EXPERIENCE</h5>
                      <div className="mb-3">
                        <p className="font-bold text-xs text-slate-800 dark:text-slate-200 mb-1">{activeJob.cv_title}</p>
                        <p className="text-[9px] text-slate-400 mb-2 font-heading">2021 — Present</p>
                        <ul className="list-disc ml-4 text-[11px] space-y-2 text-slate-600 dark:text-slate-400 font-body">
                          {activeJob.cv_content.split('. ').slice(1).map((sentence, sIdx) => (
                            <li key={sIdx} className="demo-content" dangerouslySetInnerHTML={{ __html: sentence }} />
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
              
              <div className="mt-auto pt-8 border-t border-dashed border-slate-200 dark:border-slate-800">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400 font-heading">KEYWORD MATCH</span>
                  <motion.span 
                    key={activeJobId}
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="text-xl font-bold font-heading text-green-600 dark:text-green-400"
                  >
                    {activeJob.match}%
                  </motion.span>
                </div>
                <div className="h-2 w-full bg-slate-100 dark:bg-slate-900/50 rounded-full overflow-hidden">
                  <motion.div 
                    key={activeJobId}
                    initial={{ width: 0 }}
                    animate={{ width: `${activeJob.match}%` }}
                    transition={{ duration: 1.5, ease: "easeOut", delay: 0.2 }}
                    className="h-full bg-green-500 rounded-full"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-[#FAF9F6] dark:from-[#0A0F1E] to-transparent pointer-events-none" />
      </section>

      {/* CHROME EXTENSION SECTION */}
      <section className="py-24 overflow-hidden relative bg-white dark:bg-transparent">
        <div className="container mx-auto px-6 relative z-10">
          <div className="flex flex-col lg:flex-row items-center lg:items-start gap-12">
            <div className="flex-1 space-y-10 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-3 py-0.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-600 dark:text-cyan-400 text-[9px] font-bold uppercase tracking-widest">
                Auto Apply Extension
              </div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold leading-tight">Auto Apply LinkedIn with our extension</h2>
              <p className="text-slate-600 dark:text-slate-400 text-base leading-relaxed max-w-xl mx-auto lg:mx-0 font-body">
                Alvaru is a powerful AI CV optimiser that sits quietly in your browser. When you visit a job posting, it springs to action,
                detecting the role and preparing your perfect tailored CV. It even fills the forms for you.
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-5 gap-x-6">
                {[
                  "One-click form filling",
                  "Real-time CV tailoring",
                  "Job data extraction",
                  "Syncs with dashboard"
                ].map(txt => (
                  <li key={txt} className="flex items-center gap-2 text-slate-600 dark:text-slate-300 justify-center lg:justify-start">
                    <Zap className="w-4 h-4 text-cyan-500" />
                    <span className="text-[13px] font-medium font-body">{txt}</span>
                  </li>
                ))}
              </ul>
              <Button 
                size="lg" 
                className="bg-slate-900 dark:bg-white hover:bg-slate-800 dark:hover:bg-slate-200 text-white dark:text-black font-bold text-base px-8 py-5 rounded-xl group h-auto shadow-xl transition-all font-heading"
                onClick={openChromeStore}
              >
                <ChromeStoreLogo className="w-8 h-8 mr-3" />
                Start Auto Applying for Free
              </Button>
            </div>
            
            <div className="flex-1 relative">
              <motion.div 
                initial={{ opacity: 0, scale: 0.95, rotateY: -5, rotateX: 5 }}
                whileInView={{ opacity: 1, scale: 1, rotateY: 0, rotateX: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1, ease: "easeOut" }}
                className="relative z-10"
              >
                <img 
                  src="/web.2.png" 
                  alt="Alvaru AI CV Optimiser Extension" 
                  className="w-full max-w-2xl mx-auto rounded-2xl shadow-2xl border border-slate-200 dark:border-white/10"
                  loading="lazy"
                  sizes="(max-width: 768px) 100vw, 50vw"
                />
              </motion.div>
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-cyan-500/20 rounded-full blur-[100px] -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 bg-slate-100/50 dark:bg-slate-950/50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4 tracking-tight">Loved by Job Seekers</h2>
            <p className="text-slate-500 dark:text-slate-400 text-base font-body">Real results from people who stopped wasting time and started to get more interviews.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-white dark:bg-slate-900 shadow-sm dark:shadow-lg p-7 rounded-2xl border border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 transition-colors">
                <div className="flex gap-0.5 mb-4">
                  {Array(t.rating).fill(0).map((_, j) => <Star key={j} className="w-3.5 h-3.5 fill-cyan-600 dark:fill-cyan-500 text-cyan-500" />)}
                </div>
                <p className="text-slate-700 dark:text-slate-300 text-sm italic mb-6 leading-relaxed font-body">"{t.content}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden flex-shrink-0">
                    <img src={`https://i.pravatar.cc/100?img=${i + 30}`} alt={`${t.name} - ${t.role}`} />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800 dark:text-white text-[13px]">{t.name}</h3>
                    <p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold font-heading">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-24 bg-white dark:bg-transparent">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4 tracking-tight">Simple Pricing for More Interviews</h2>
            <p className="text-slate-500 dark:text-slate-400 text-base font-body">Pick the AI CV optimiser plan that fits your search frequency.</p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-5xl mx-auto items-center">
            {/* Free */}
            <div className="bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-2xl p-7 flex flex-col h-full relative group shadow-sm dark:shadow-none transition-all">
              <div className="mb-6">
                <h3 className="text-cyan-600 dark:text-cyan-500 text-[9px] font-bold uppercase tracking-[0.2em] mb-1 font-heading">Free</h3>
                <div className="text-2xl font-bold font-heading">$0<span className="text-xs text-slate-500 font-normal"> / month</span></div>
              </div>
              <ul className="space-y-3 mb-10 flex-1">
                {[
                  "5 CV rewrites per month",
                  "Chrome extension access",
                  "PDF download",
                  "Application tracker"
                ].map(f => (
                  <li key={f} className="flex items-center gap-2.5 text-xs text-slate-500 font-body">
                    <Check className="w-3.5 h-3.5 text-cyan-600 dark:text-cyan-500" /> {f}
                  </li>
                ))}
              </ul>
              <Button onClick={() => navigate("/app")} variant="outline" className="w-full py-5 uppercase font-bold tracking-widest text-[9px] border-slate-200 dark:border-slate-700 h-auto font-heading">Start Free</Button>
            </div>

            {/* Pro */}
            <div className="bg-white dark:bg-slate-900 border-2 border-cyan-500 rounded-2xl p-8 flex flex-col h-full relative shadow-xl dark:shadow-[0_0_30px_rgba(0,212,255,0.1)] z-10 transition-all">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-cyan-500 text-white dark:text-black text-[9px] font-bold px-3 py-0.5 rounded-full uppercase font-heading">Most Popular</div>
              <div className="mb-6">
                <h3 className="text-cyan-600 dark:text-cyan-500 text-[9px] font-bold uppercase tracking-[0.2em] mb-1 font-heading">Pro</h3>
                <div className="text-2xl font-bold font-heading">$9<span className="text-xs text-slate-500 font-normal"> / month</span></div>
              </div>
              <ul className="space-y-3 mb-10 flex-1 font-body">
                {[
                  "200 CV rewrites per month",
                  "Chrome extension access",
                  "Unlimited auto-apply",
                  "Priority support",
                  "Cancel anytime"
                ].map(f => (
                  <li key={f} className="flex items-center gap-2.5 text-[13px] text-slate-800 dark:text-slate-200">
                    <Check className="w-3.5 h-3.5 text-cyan-600 dark:text-cyan-500" /> {f}
                  </li>
                ))}
              </ul>
              <Button 
                onClick={() => handleSubscribe("price_1TPMFO85c4rhaB2kdbd5Alhl", "Pro")} 
                disabled={isSubscribing === "price_1TPMFO85c4rhaB2kdbd5Alhl"}
                className="w-full py-5 bg-cyan-600 dark:bg-cyan-500 hover:bg-cyan-700 dark:hover:bg-cyan-400 text-white dark:text-black uppercase font-bold tracking-widest text-[9px] h-auto font-heading transition-all"
              >
                {isSubscribing === "price_1TPMFO85c4rhaB2kdbd5Alhl" ? "Redirecting..." : "Go Pro"}
              </Button>
            </div>

            {/* Elite */}
            <div className="bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-2xl p-7 flex flex-col h-full relative group shadow-sm dark:shadow-none transition-all">
              <div className="mb-6">
                <h3 className="text-cyan-600 dark:text-cyan-500 text-[9px] font-bold uppercase tracking-[0.2em] mb-1 font-heading">Elite</h3>
                <div className="text-2xl font-bold font-heading">$19<span className="text-xs text-slate-500 font-normal"> / month</span></div>
              </div>
              <ul className="space-y-3 mb-10 flex-1 font-body text-xs">
                {[
                  "800 CV rewrites per month",
                  "Chrome extension access",
                  "Unlimited auto-apply",
                  "Multiple base CVs",
                  "Priority AI processing",
                  "Cancel anytime"
                ].map(f => (
                  <li key={f} className="flex items-center gap-2.5 text-slate-500">
                    <Check className="w-3.5 h-3.5 text-cyan-600 dark:text-cyan-500" /> {f}
                  </li>
                ))}
              </ul>
              <Button 
                onClick={() => handleSubscribe("price_1TPMGu85c4rhaB2kIyrtQMF7", "Elite")} 
                disabled={isSubscribing === "price_1TPMGu85c4rhaB2kIyrtQMF7"}
                variant="outline" 
                className="w-full py-5 uppercase font-bold tracking-widest text-[9px] border-slate-200 dark:border-slate-700 h-auto font-heading"
              >
                {isSubscribing === "price_1TPMGu85c4rhaB2kIyrtQMF7" ? "Redirecting..." : "Go Elite"}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-slate-100/50 dark:bg-slate-950/30">
        <div className="container mx-auto px-6 max-w-3xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4 tracking-tight text-slate-800 dark:text-white">FAQ — AI CV Optimiser & Auto Apply</h2>
            <p className="text-slate-500 dark:text-slate-400 text-[10px] uppercase tracking-widest font-bold font-heading">Common questions about getting more interviews with Alvaru.</p>
          </div>
          
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white dark:bg-slate-900/40 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden shadow-sm transition-all">
                <button 
                  onClick={() => setActiveFaq(activeFaq === i ? null : i)}
                  className="w-full px-6 py-5 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-900 transition-colors"
                >
                  <h3 className="font-bold text-sm text-left font-heading text-slate-800 dark:text-white">{faq.q}</h3>
                  {activeFaq === i ? <ChevronUp className="w-4 h-4 text-cyan-600 dark:text-cyan-400" /> : <ChevronDown className="w-4 h-4 text-slate-400 dark:text-slate-600" />}
                </button>
                <div className={`px-6 pb-5 text-slate-600 dark:text-slate-400 text-[13px] leading-relaxed border-t border-slate-100 dark:border-slate-800/30 pt-4 font-body ${activeFaq === i ? 'block' : 'hidden'}`}>
                  {faq.a}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="pt-24 pb-12 border-t border-slate-200 dark:border-slate-900 bg-white dark:bg-[#060912]">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
            <div className="space-y-5">
              <div className="flex items-center mb-6">
                <img src="/logo.png" alt="Alvaru logo" className="h-24 w-auto" />
              </div>
              <p className="text-slate-500 dark:text-slate-500 text-xs leading-relaxed font-body">
                The ultimate AI CV optimiser with auto apply LinkedIn capabilities. 
                Land your dream role and get more interviews in half the time.
              </p>
              <div className="flex gap-4">
                <a href="#" className="text-slate-400 hover:text-cyan-600 transition-colors" aria-label="Website"><Globe className="w-4 h-4" /></a>
                <a href="#" className="text-slate-400 hover:text-cyan-600 transition-colors" aria-label="Users"><Users className="w-4 h-4" /></a>
                <a href="#" className="text-slate-400 hover:text-cyan-600 transition-colors" aria-label="Contact"><MessageSquare className="w-4 h-4" /></a>
              </div>
            </div>
            
            <div>
              <h3 className="font-bold text-[9px] uppercase tracking-[0.2em] text-slate-400 mb-6 font-heading">Platform</h3>
              <ul className="space-y-3">
                <li><a href="#how-it-works" className="text-xs text-slate-500 hover:text-cyan-600 transition-colors font-body">How it works</a></li>
                <li><a href="https://chromewebstore.google.com/detail/alvaru-smart-job-apply/laoahcalgdmfecdbiodloilhcafeoafl" target="_blank" className="text-xs text-slate-500 hover:text-cyan-600 transition-colors font-body">Chrome Extension</a></li>
                <li><a href="#pricing" className="text-xs text-slate-500 hover:text-cyan-600 transition-colors font-body">Pricing</a></li>
                <li><Link to="/blog" className="text-xs text-slate-500 hover:text-cyan-600 transition-colors font-body">Blog</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-bold text-[9px] uppercase tracking-[0.2em] text-slate-400 mb-6 font-heading">Legal</h3>
              <ul className="space-y-3">
                <li><Link to="/privacy" className="text-xs text-slate-500 hover:text-cyan-600 transition-colors font-body">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-xs text-slate-500 hover:text-cyan-600 transition-colors font-body">Terms of Service</Link></li>
                <li><Link to="/cookie-policy" className="text-xs text-slate-500 hover:text-cyan-600 transition-colors font-body">Cookie Policy</Link></li>
                <li><a href="mailto:info@alvaru.com" className="text-xs text-slate-500 hover:text-cyan-600 transition-colors font-body">Contact</a></li>
              </ul>
            </div>
            
            <div className="space-y-6">
              <div>
                <h3 className="font-bold text-[9px] uppercase tracking-[0.2em] text-slate-400 mb-6 font-heading">Install Auto Apply</h3>
                <button onClick={openChromeStore} className="w-full text-left">
                  <div className="bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl p-3 flex items-center gap-3 hover:border-cyan-500/30 transition-all group">
                    <div className="w-10 h-10 rounded-lg bg-white dark:bg-slate-900 flex items-center justify-center shadow-sm"><ChromeStoreLogo className="w-7 h-7" /></div>
                    <div>
                      <p className="text-[8px] font-bold text-slate-400 uppercase font-heading">Chrome store</p>
                      <p className="text-[11px] font-bold text-slate-900 dark:text-white font-body">Add to Browser</p>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col md:flex-row items-center justify-between pt-8 border-t border-slate-200 dark:border-slate-900/50 gap-6">
            <div className="flex items-center gap-4">
              <p className="text-[9px] text-slate-500 font-bold uppercase tracking-widest font-heading">Alvaru AI © 2026</p>
              <a href="https://aitoolhunt.co/item/alvaru" target="_blank" rel="noopener noreferrer">
                <img src="https://aitoolhunt.co/badge-listed-light.svg" alt="Listed on AIToolHunt" className="h-8 w-auto" />
              </a>
            </div>
            <div className="flex gap-6 items-center">
              <a href="https://www.tiktok.com/@alvaruai" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-cyan-600 transition-all hover:scale-110" aria-label="TikTok">
                <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.17-2.89-.6-4.13-1.47-.13 3.42-.14 6.84-.13 10.26-.03 2.04-.61 4.14-2.02 5.64-1.52 1.63-3.82 2.3-5.98 2.05-2.12-.21-4.14-1.35-5.27-3.15-1.34-2.11-1.31-5.02.13-7.04 1.25-1.78 3.41-2.77 5.59-2.58.01 1.55-.01 3.1.01 4.65-1-.14-2.09.11-2.88.82-.78.71-1.07 1.83-.78 2.82.26 1.05 1.18 1.84 2.25 1.95.84.1 1.71-.16 2.32-.76.71-.7 1.01-1.74.96-2.73-.02-4.57-.01-9.14-.02-13.71z"/>
                </svg>
              </a>
              <a href="https://www.facebook.com/alvaruAI" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-cyan-600 transition-colors" aria-label="Facebook">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="https://www.instagram.com/alvaruai/" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-cyan-600 transition-colors" aria-label="Instagram">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="https://www.linkedin.com/company/alvaru" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-cyan-600 transition-colors" aria-label="LinkedIn">
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
